import { useCallback, useEffect, useMemo, useState } from "react";

import { PageHead } from "./Shell";
import { formatDate } from "./Records";
import { AdminError, admin, type WebhookCatalog, type WebhookRow } from "./api";

/**
 * Powiadomienia dla automatyzacji zlecającego.
 *
 * Wcześniej adres wpisywało się w plik procesu: jeden na zdarzenie, niewidoczny
 * w panelu i kasowany przy każdym imporcie schematu. Tu superadministrator sam
 * dopina scenariusz z Make do zdarzenia — i widzi, czy ostatnia wysyłka doszła.
 */
export function Webhooks() {
  const [rows, setRows] = useState<WebhookRow[]>([]);
  const [catalog, setCatalog] = useState<WebhookCatalog | null>(null);
  const [event, setEvent] = useState("");
  const [schemaId, setSchemaId] = useState("");
  const [datapointKey, setDatapointKey] = useState("");
  const [url, setUrl] = useState("");
  const [includeValue, setIncludeValue] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [lista, katalog] = await Promise.all([admin.webhooks(), admin.webhookCatalog()]);
      setRows(lista.items);
      setCatalog(katalog);
      setError(null);
    } catch (caught) {
      setError((caught as AdminError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const wybraneZdarzenie = catalog?.events.find((e) => e.id === event);
  const naPytanie = Boolean(wybraneZdarzenie?.datapoint);
  const proces = catalog?.processes.find((p) => p.schema_id === schemaId);

  // Zmiana zdarzenia albo procesu unieważnia wybrane pytanie — inaczej zostałby
  // klucz z poprzedniego procesu, który w nowym znaczy co innego albo nie istnieje.
  const wybierzZdarzenie = (id: string) => {
    setEvent(id);
    setDatapointKey("");
    setIncludeValue(false);
  };
  const wybierzProces = (id: string) => {
    setSchemaId(id);
    setDatapointKey("");
  };

  const gotowe = Boolean(event && url.trim() && (!naPytanie || (schemaId && datapointKey)));

  const add = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await admin.addWebhook({
        event,
        url: url.trim(),
        schema_id: schemaId || null,
        datapoint_key: naPytanie ? datapointKey : null,
        include_value: naPytanie && includeValue,
      });
      setUrl("");
      setDatapointKey("");
      setIncludeValue(false);
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  const zadanie = async (akcja: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await akcja();
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  const test = (row: WebhookRow) =>
    zadanie(async () => {
      const wynik = await admin.testWebhook(row.id);
      setNotice(
        wynik.delivered
          ? `Próba doszła (${wynik.detail}). W scenariuszu rozpoznasz ją po parametrze test=1.`
          : `Próba nie doszła: ${wynik.detail}. Sprawdź adres i czy scenariusz jest włączony.`,
      );
    });

  const przelaczOdpowiedz = (row: WebhookRow) => {
    // Włączenie zmienia, jakie dane wychodzą na zewnątrz — wymaga świadomego
    // kliknięcia. Wyłączenie tylko ogranicza, więc idzie od razu.
    if (
      !row.include_value &&
      !window.confirm(
        "Odpowiedź na to pytanie zacznie wychodzić pod ten adres przy każdym wypełnieniu. Włączyć?",
      )
    )
      return;
    void zadanie(() => admin.updateWebhook(row.id, { include_value: !row.include_value }));
  };

  const remove = (row: WebhookRow) => {
    if (!window.confirm(`Usunąć webhook „${row.event_label}"? Powiadomienia przestaną wychodzić od razu.`)) return;
    void zadanie(() => admin.deleteWebhook(row.id));
  };

  // Tytuły pytań po kluczu i procesie — lista pokazuje „PESEL", nie „pesel".
  const tytulPytania = useMemo(() => {
    const mapa = new Map<string, string>();
    catalog?.processes.forEach((p) =>
      p.datapoints.forEach((d) => mapa.set(`${p.schema_id}:${d.key}`, d.title)),
    );
    return (row: WebhookRow) => mapa.get(`${row.schema_id}:${row.datapoint_key}`) ?? row.datapoint_key;
  }, [catalog]);

  const tytulProcesu = (id: string | null) =>
    id ? (catalog?.processes.find((p) => p.schema_id === id)?.title ?? id) : "wszystkie procesy";

  return (
    <section className="adm__section">
      <PageHead
        title="Webhooki"
        subtitle="Powiadomienia dla automatyzacji — np. scenariusza w Make — o zdarzeniach w sprawach"
      />

      <div className="adm__toolbar">
        <select
          className="select"
          aria-label="Zdarzenie"
          value={event}
          onChange={(e) => wybierzZdarzenie(e.target.value)}
        >
          <option value="">Wybierz zdarzenie…</option>
          {catalog?.events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.label}
            </option>
          ))}
        </select>

        {event && (
          <select
            className="select"
            aria-label="Proces"
            value={schemaId}
            onChange={(e) => wybierzProces(e.target.value)}
          >
            {/* Pytania mają sens tylko w ramach procesu — ten sam klucz w innym
                procesie znaczy co innego — więc tam „wszystkie" nie wchodzi w grę. */}
            <option value="">{naPytanie ? "Wybierz proces…" : "Wszystkie procesy"}</option>
            {catalog?.processes.map((p) => (
              <option key={p.schema_id} value={p.schema_id}>
                {p.title}
              </option>
            ))}
          </select>
        )}

        {naPytanie && (
          <select
            className="select"
            aria-label="Pytanie"
            value={datapointKey}
            disabled={!proces}
            onChange={(e) => setDatapointKey(e.target.value)}
          >
            <option value="">{proces ? "Wybierz pytanie…" : "Najpierw wybierz proces"}</option>
            {proces?.datapoints.map((d) => (
              <option key={d.key} value={d.key}>
                {d.title} ({d.key})
              </option>
            ))}
          </select>
        )}

        {naPytanie && (
          <label className="adm__check">
            <input
              type="checkbox"
              checked={includeValue}
              onChange={(e) => setIncludeValue(e.target.checked)}
            />
            Wysyłaj odpowiedź
          </label>
        )}

        <input
          className="input"
          type="url"
          aria-label="Adres webhooka"
          placeholder="https://hook.eu1.make.com/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && gotowe) void add();
          }}
        />
        <button className="btn btn--primary" disabled={busy || !gotowe} onClick={add}>
          Dodaj webhook
        </button>
      </div>

      {/* Ostrzeżenie widać w chwili zaznaczenia, a nie w dokumentacji, której
          nikt nie otworzy przed kliknięciem „Dodaj". */}
      {naPytanie && includeValue && (
        <div className="adm__alert adm__alert--warn">
          Odpowiedź pojedzie w adresie powiadomienia. Adresy automatyzacji bywają zapisywane w logach po
          drodze — włączaj dla pytań bez danych wrażliwych, nie dla PESEL-u, numeru dokumentu czy rachunku.
          Przy pytaniu o plik wysyłamy nazwę pliku, sam dokument pobiera się przez API.
        </div>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="adm__muted">{notice}</p>}

      <div className="adm__tablewrap">
        <table className="adm__table">
          <thead>
            <tr>
              <th>Zdarzenie</th>
              <th>Adres</th>
              <th>Ostatnia wysyłka</th>
              <th />
            </tr>
          </thead>
          <tbody className="kaskada">
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={row.active ? undefined : "adm__muted"}
                style={{ "--i": index } as React.CSSProperties}
              >
                <td>
                  {row.event_label}
                  {row.datapoint_key && <div className="adm__muted">{tytulPytania(row)}</div>}
                  <div className="adm__muted">{tytulProcesu(row.schema_id)}</div>
                  {row.include_value && <span className="pill pill--warn">z odpowiedzią</span>}
                  {!row.active && <span className="pill pill--not_started">wstrzymany</span>}
                </td>
                {/* Adres Make zawiera token w ścieżce. Ta zakładka jest tylko dla
                    superadministratora, więc pokazujemy go w całości — ale ucięty,
                    żeby nie wisiał na ekranie przy każdym otwarciu listy. */}
                <td className="adm__url" title={row.url}>
                  {row.url}
                </td>
                <td>
                  {row.last_status ? (
                    <>
                      <span className={`pill ${row.last_status === "ok" ? "pill--checked" : "pill--rejected"}`}>
                        {row.last_status === "ok" ? "doszła" : "nie doszła"}
                      </span>
                      <div className="adm__muted adm__date">
                        {row.last_detail}
                        {row.last_at && ` · ${formatDate(row.last_at)}`}
                      </div>
                    </>
                  ) : (
                    <span className="adm__muted">jeszcze nic nie wysłano</span>
                  )}
                </td>
                <td className="adm__right">
                  {/* Flex na samej komórce tabeli psuje jej układ — przyciski w środku. */}
                  <div className="adm__actions adm__actions--end">
                    <button className="btn btn--ghost" disabled={busy} onClick={() => test(row)}>
                      Wyślij test
                    </button>
                    {row.event === "datapoint" && (
                      <button className="btn btn--ghost" disabled={busy} onClick={() => przelaczOdpowiedz(row)}>
                        {row.include_value ? "Bez odpowiedzi" : "Wysyłaj odpowiedź"}
                      </button>
                    )}
                    <button
                      className="btn btn--ghost"
                      disabled={busy}
                      onClick={() => zadanie(() => admin.updateWebhook(row.id, { active: !row.active }))}
                    >
                      {row.active ? "Wstrzymaj" : "Wznów"}
                    </button>
                    <button className="btn btn--ghost adm__risky" disabled={busy} onClick={() => remove(row)}>
                      Usuń
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="adm__muted">
                  Żadnego webhooka — powiadomienia nigdzie nie wychodzą.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Osoba budująca scenariusz musi wiedzieć, co przyjdzie, zanim cokolwiek
          się wydarzy — inaczej mapuje pola na ślepo i czeka na prawdziwą sprawę. */}
      <details className="adm__help">
        <summary>Co przychodzi w powiadomieniu</summary>
        <p>
          Zapytanie <code>GET</code> pod podany adres, dane w parametrach. Zawsze: <code>event</code>,{" "}
          <code>record_id</code>, <code>panel_url</code> — link do karty sprawy w panelu.
        </p>
        <ul>
          <li>
            <strong>Zmiana statusu procesu</strong> — <code>status</code> i <code>previous</code>:{" "}
            {catalog &&
              Object.entries(catalog.statuses)
                .map(([id, label]) => `${id} (${label})`)
                .join(", ")}
            .
          </li>
          <li>
            <strong>Wypełnione konkretne pytanie</strong> — <code>key</code> i <code>source</code>: kto
            wypełnił (<code>user</code> kandydat, <code>consultant</code> konsultant, <code>verified</code>{" "}
            odczyt z dokumentu). Domyślnie bez samej odpowiedzi — adresy automatyzacji bywają logowane po
            drodze, więc pobiera się ją przez API po <code>record_id</code>. Przy włączonym{" "}
            <strong>Wysyłaj odpowiedź</strong> dochodzi <code>value</code>.
          </li>
          <li>
            Próba z przycisku <strong>Wyślij test</strong> ma dodatkowo <code>test=1</code>.
          </li>
        </ul>
        <p>Wysyłamy dopiero po zapisaniu zmiany — nigdy o czymś, co się wycofało.</p>
      </details>
    </section>
  );
}
